 
'use client';

import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useRef, useEffect } from "react";
import { useTranslations } from 'next-intl';
import Poster, { type PosterApi } from "../base/Poster";
import useShare from "../../hooks/useShare";
import Image from "next/image";
import { Drawer, DrawerContent, DrawerTitleHeader } from "@/components/ui/drawer";
import useWelfare from "../../hooks/useWelfare";
import ShareActionGroup from "./ShareActionGroup";
import toast from "react-hot-toast";
import { copyToClipboard } from "@/utils/copy";
import { useClickAway } from "ahooks";
import { track } from "@/utils/track";
import { setUrlQueryParams } from "@/utils/url";
import { ShareSourceEnum } from "@/types/invited";
import { useSetAtom } from "jotai";
import { shouldRenderDownloadPosterAtom } from "@/store/referral";

export interface ShareModalProps {
  source?: string;
}

const ShareModal = NiceModal.create<ShareModalProps>(({ }) => {
  const modal = useModal();
  const t = useTranslations('referral');
  const posterRef = useRef<PosterApi>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const posterContainerRef = useRef<HTMLDivElement>(null);
  const { shareConfigData, shareUrl, inviteCode, activityEnabled, downloadPosterRef, shareText } = useShare();
  const setShouldRenderDownloadPoster = useSetAtom(shouldRenderDownloadPosterAtom);
  const { inviteAmountText } = useWelfare();

  // 当 modal 打开时，预渲染下载用的 Poster
  useEffect(() => {
    if (modal.visible) {
      setShouldRenderDownloadPoster(true);
    }
  }, [modal.visible, setShouldRenderDownloadPoster]);

  const handleClose = () => {
    modal.hide();
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const handleCopy = async (text: string, type: 'code' | 'shareUrl') => {
    if (!activityEnabled) {
      toast.error(t('eventEnded'));
      return;
    }
    try {
      if (!text) {
        return;
      }
      const trackParamsMap = {
        code: 'copy invite code',
        shareUrl: 'copy invite link'
      };
      track('share_button_click', {
        invite_activity: 3,
        page_type: 'popup',
        button_name: trackParamsMap[type]
      });
      if (type === 'shareUrl') {
        const shareTexts = `${shareText} ${text}`;
        await copyToClipboard(shareTexts);
      } else {
        await copyToClipboard(text);
      }
      toast.success(t('copySuccess'));
    } catch {
      toast.error(t('copyFailed'));
    }
  };

  const handleDownload = () => {
    track('share_button_click', {
      invite_activity: 3,
      page_type: 'popup',
      button_name: 'download poster'
    });

    if (!downloadPosterRef?.current) {
      console.warn('====> Poster ref is not available for download');
      return;
    }

    console.log('====> Downloading poster using global downloadPosterRef');
    downloadPosterRef.current.handleDownloadPoster();
  };

  const onShareToPlatform = (platform: string) => {
    track('share_button_click', {
      invite_activity: 3,
      page_type: 'popup',
      button_name: platform
    });
  };

  const posterUrl = shareConfigData?.poster_image || '';
  const qrcodeUrl = shareUrl || '';

  // 点击非海报和 Drawer 区域时关闭弹窗
  useClickAway((e) => {
    const target = e.target as Element;
    const ignoreId = posterRef.current?.getDownloadLinkId();
    if (ignoreId === target.getAttribute('id')) {
      return;
    }
    if (modal.visible) {
      handleClose();
    }
  }, [posterContainerRef, drawerRef]);

  const posterHeight = () => {
    const innerWidth = window.innerWidth;
    // 314底部栏=274+上下边距40
    const innerHeight = window.innerHeight - 314;
    const ratio = 9 / 16;
    // 104=左右边距和
    const width = innerWidth - 104;
    const height = width / ratio;
    return Math.min(innerHeight, height);
  };

  // 当 modal 不可见时，不渲染任何内容
  if (!modal.visible) {
    return null;
  }

  return (
    <>
      {/* 全屏蒙层 + 海报展示 */}
      <div className="fixed top-0 inset-0 bg-black/85 z-[70] flex justify-center">
        {/* 海报居中展示 */}
        <div className="w-full flex items-center mx-[52px] bg-[rgba(0,0,0,0.6)] py-[20px]" style={{ height: 'calc(100% - 274px)' }}>
          <div className="w-full overflow-hidden" style={{ height: `${posterHeight()}px` }}>
            <div ref={posterContainerRef} className="w-full h-full">
              <Poster
                ref={posterRef}
                className="w-full h-full"
                poster={{
                  url: posterUrl,
                  auto: true,
                  className: 'h-[auto]'
                }}
                qrcode={{
                  url: qrcodeUrl,
                  size: '22.9vw',
                }}
                logo={{
                  width: 56,
                  height: 31
                }}
                isThumbnail={false}
                inviteCode={inviteCode || ''}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 底部 Drawer - 分享信息 */}
      <Drawer
        open={modal.visible}
        onOpenChange={handleDrawerOpenChange}
        modal={false}
      >
        <DrawerContent ref={drawerRef} className="bg-surface border-none z-[71] h-[274px] overflow-hidden">
          {/* Drawer 头部 */}
          <DrawerTitleHeader title={t('inviteToEarnCash', { amount: inviteAmountText })} showClose className="leading-3" />

          {/* 分享信息 */}
          <div className="px-3 pb-5">
            <div className="flex justify-between items-center bg-dark-special rounded-lg px-4 h-[55px] mb-2.5">
              <div className="text-tertiary text-xs font-medium shrink-0 leading-[10px]">{t('referralCode')}</div>
              <div className="flex-1 flex items-center justify-end" onClick={() => handleCopy(inviteCode || '', 'code')}>
                <div className="text-xs font-medium text-white mr-2 leading-[10px]">{inviteCode}</div>
                <Image
                  src="/assets/images/ic_copy.png"
                  alt="copy"
                  width={10}
                  height={10}
                  className="w-2.5 h-2.5 ml-1  shrink-0 active:scale-90"
                />
              </div>
            </div>

            <div className="flex justify-between items-center bg-dark-special rounded-lg px-4 h-[55px]">
              <div className="text-tertiary text-xs font-medium block shrink-0 leading-[10px]">{t('shareLink')}</div>
              <div className="flex-1 flex items-center justify-end grow w-0"
                onClick={() => handleCopy(shareUrl ? setUrlQueryParams(shareUrl, { s: ShareSourceEnum.Website }) : '', 'shareUrl')}>
                <div className="text-xs font-medium text-white mr-2 break-words whitespace-nowrap overflow-hidden text-ellipsis line-clamp-1 pl-4 grow w-0">
                  {shareUrl || ''}
                </div>
                <Image
                  src="/assets/images/ic_copy.png"
                  alt="copy"
                  width={10}
                  height={10}
                  className="w-2.5 h-2.5 ml-1  shrink-0 active:scale-90"
                />
              </div>
            </div>
          </div>

          <ShareActionGroup className="mb-[30px] w-full" handleDownloadPoster={handleDownload} onShare={onShareToPlatform} />
        </DrawerContent >
      </Drawer>
    </>
  );
});

export default ShareModal;